import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import moment from 'moment';
import { asapScheduler, scheduled, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DispatchDriverSummary } from 'src/app/classes/dispatch-driver-summary';
import { DispatchTaskDetails } from 'src/app/classes/dispatch-task-details';
import { DispatchTaskGroupSummary } from 'src/app/classes/dispatch-task-group-summary';
import { DispatchTaskStatus } from 'src/app/classes/dispatch-task-status';
import { DispatchUpdateResponse } from 'src/app/classes/dispatch-update-response';
import { Profile } from 'src/app/classes/profile';
import { TaskType } from 'src/app/classes/task-type-enum';
import { TerminalRunTask } from 'src/app/classes/terminal-run-task';
import { HH_MM_CTRLS } from 'src/app/common/constants';
import { DispatchService } from 'src/app/services/dispatch.service';
import { LoggerService } from 'src/app/services/logger.service';
import { MyTaskConfirmationDialogComponent } from '../my-tasks/my-task-confirmation-dialog/my-task-confirmation-dialog.component';
import { ProfileService } from './../../services/profile.service';
import { UtilitiesService } from './../../services/utilities.service';
import { NgbDateDispatchParserFormatter2 } from './ngb-date-custom-parser';

export interface Task {
  name: string;
  completed: boolean;
  subtasks?: Task[];
}
@Component({
  standalone: false,
  selector: 'app-dispatch',
  templateUrl: './dispatch.component.html',
  styleUrls: [ './dispatch.component.scss' ],
  providers: [ { provide: NgbDateParserFormatter, useClass: NgbDateDispatchParserFormatter2 } ],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class DispatchComponent implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) private virtualScroll: CdkVirtualScrollViewport;
  public TaskType = TaskType;
  public dispatchForm: FormGroup = this.fb.group({
    otherStartTime: this.fb.control(''),
    otherEndTime: this.fb.control(''),
    coordinatorNotes: this.fb.control(''),
    driverNotes: this.fb.control({ value: '', disabled: true }),
    driverSummary: this.fb.control(''),
    assignedDriver: this.fb.control(''),
    pickUp: this.fb.control(''),
    dropOff: this.fb.control(''),
    unassignedTypes: this.fb.array([])
  });
  public taskGroupSummaryList: DispatchTaskGroupSummary[];
  public oooiTableFields: { header: string; oooiKey: 'TASK' | 'STA_STD' | 'ETA_ETD' | 'ON_OUT' | 'IN_OFF' }[] = [];
  public preferredDriversList: string[];
  public taskDetailsList: DispatchTaskDetails[];
  public expandedId: string;

  loading = true;
  subs = new Subscription();

  constructor(
    private logger: LoggerService,
    private fb: FormBuilder,
    private dispatchService: DispatchService,
    public profileService: ProfileService,
    private utilityService: UtilitiesService,
    readonly dialog: MatDialog,
    private ref: ChangeDetectorRef
  ) {
    // setInterval(() => {
    // //  this.numberOfTicks++;
    //   // require view to be updated
    // //  this.ref.markForCheck();
    // }, 3000);
   // this.ref.markForCheck();
  }

  // afterResize() {
  //   this.virtualScroll.refresh();
  //   console.log("virtual scroll refreshed");
  // }


  ngOnInit(): void {
    this.subs.add(this.subscribeToDispatchData());
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private subscribeToDispatchData(): Subscription {
    return this.dispatchService.getDispatchData().subscribe((dispatchData) => {
      if (dispatchData) {
        this.taskGroupSummaryList = dispatchData['taskGroupSummaryList'] || [];
        this.preferredDriversList = dispatchData['preferredDriversList'];
      }

      if (!(this.taskGroupSummaryList || []).find((task: DispatchTaskGroupSummary) => this.getTaskKey(task) === this.expandedId)) {
        this.expandedId = undefined;
      }

      this.loading = false;
      // this.ref.markForCheck();
      // this.afterResize();
    });
  }

  public buildOooiTable(
    taskDetails: DispatchTaskDetails
  ): { header: string; oooiKey: 'TASK' | 'STA_STD' | 'ETA_ETD' | 'ON_OUT' | 'IN_OFF' }[] {
    if (taskDetails) {
      const isInbound = taskDetails.taskTypeName === 'InboundFlight';
      return [
        { header: 'TASK', oooiKey: 'TASK' },
        { header: isInbound ? 'STA' : 'STD', oooiKey: 'STA_STD' },
        { header: isInbound ? 'ETA' : 'ETD', oooiKey: 'ETA_ETD' },
        { header: isInbound ? 'ON' : 'OUT', oooiKey: 'ON_OUT' },
        { header: isInbound ? 'IN' : 'OFF', oooiKey: 'IN_OFF' }
      ];
    }
    return [];
  }

  timeMath(thisDate: string): string[] {
    return this.utilityService.timeMath(thisDate);
  }

  getDriverSummaryDisplayName(driverSummary: DispatchDriverSummary): string {
    return this.utilityService.getDriverSummaryDisplayName(driverSummary);
  }

  getTaskDetails(task: DispatchTaskGroupSummary): void {
    if (this.expandedId !== this.getTaskKey(task)) {
      this.updateTaskDetails(undefined);
    }
    this.expandedId = this.getTaskKey(task);
    const taskId = [ TaskType.InboundFlight, TaskType.OutboundFlight ].some(
      (taskType: TaskType) => taskType.toString() === task.taskType
    )
      ? task.flightId
      : task.taskName;
    this.dispatchService
      .getTaskDetails(taskId, task.taskType)
      .subscribe((taskDetails: DispatchUpdateResponse | DispatchTaskDetails) => {
        if (taskDetails) {
          this.updateTaskDetails(
            (taskDetails as DispatchUpdateResponse).taskList || [ taskDetails as DispatchTaskDetails ]
          );
        }
      });
  }

  updateTask(task: DispatchUpdateResponse): void {
    if (task) {
      let hasCompletedTask = false;
      task.taskGroupSummary.forEach((updatedSummary: DispatchTaskGroupSummary) => {
        const foundSummary: DispatchTaskGroupSummary = this.taskGroupSummaryList.find(
          (taskSummary: DispatchTaskGroupSummary) => {
            return updatedSummary.flightId
              ? taskSummary.flightId === updatedSummary.flightId
                && taskSummary.destination === updatedSummary.destination
              : updatedSummary.taskName === taskSummary.taskName;
          }
        );
        if (foundSummary) {
          Object.keys(updatedSummary).forEach((key: string) => (foundSummary[key] = updatedSummary[key]));
        }
        if (updatedSummary.taskGroupStatus === DispatchTaskStatus.COMPLETED
          || updatedSummary.taskGroupStatus === DispatchTaskStatus.CANCELLED) {
          hasCompletedTask = true;
        }
      });

      this.updateTaskDetails(task.taskList);

      if (hasCompletedTask) {
        this.dispatchService.triggerDispatchUpdate();
      }
    }
  }

  updateTaskDetails(taskDetailsList: DispatchTaskDetails[]): void {
    this.taskDetailsList = taskDetailsList;
    const firstTaskDetails: DispatchTaskDetails = taskDetailsList && taskDetailsList[0];
    const metadata: any = firstTaskDetails && this.taskDetailsList[0].metadata;

    const specialInstructions: string =
      (metadata && metadata.specialInstructions && this.utilityService.formatNotes(metadata.specialInstructions)) || '';
    const driverNotes: string =
      (metadata && metadata.driverNotes && this.utilityService.formatNotes(metadata.driverNotes)) || '';
    const plannedStartTime: string =
      (firstTaskDetails &&
        firstTaskDetails.plannedStartTime &&
        moment(firstTaskDetails.plannedStartTime).format('HH:mm')) ||
      '';
    const plannedEndTime: string =
      (firstTaskDetails &&
        firstTaskDetails.plannedEndTime &&
        moment(firstTaskDetails.plannedEndTime).format('HH:mm')) ||
      '';

    this.dispatchForm.get('coordinatorNotes').setValue(specialInstructions);
    this.dispatchForm.get('driverNotes').setValue(driverNotes);
    this.dispatchForm.get('otherStartTime').setValue(plannedStartTime);
    this.dispatchForm.get('otherEndTime').setValue(plannedEndTime);

    const taskType: string = firstTaskDetails && firstTaskDetails.taskTypeName;

    this.oooiTableFields =
      taskType === TaskType.InboundFlight || taskType === TaskType.OutboundFlight
        ? this.buildOooiTable(this.taskDetailsList[0])
        : [];
  }

  allTaskItemsAssigned(taskDetailsList: DispatchTaskDetails[]): boolean {
    return (taskDetailsList || []).every((taskDetails: DispatchTaskDetails) => taskDetails.status !== 'Unassigned');
  }

  endRemoteParkingTask(remoteTask: DispatchTaskGroupSummary): void {
    this.dialog
      .open(MyTaskConfirmationDialogComponent, {
        width: '550px',
        data: {
          message: 'Are you sure?'
        }
      })
      .afterClosed()
      .pipe(
        switchMap(
          (response) =>
            response
              ? this.profileService
                  .getProfileObs()
                  .pipe(
                    switchMap((profile: Profile) => this.dispatchService.endRemoteParkingTask(remoteTask, profile.adId))
                  )
              : scheduled([], asapScheduler)
        )
      )
      .subscribe();
  }

  clearTaskDetails(taskKey: string): void {
    if (this.expandedId === taskKey) {
      this.expandedId = undefined;
    }
  }

  taskTrackBy = (_: number, task: DispatchTaskGroupSummary): string => {
    return this.getTaskKey(task);
  }

  updateDispatchTasks(): void {
    this.loading = true;
    this.dispatchService.triggerDispatchUpdate();
    this.getTaskDetails(
      this.taskGroupSummaryList.find((task: DispatchTaskGroupSummary) => this.getTaskKey(task) === this.expandedId)
    );
  }

  getTaskKey(task: DispatchTaskGroupSummary): string {
    return task ? `${task.displayName}_${task.origin}_${task.destination}` : undefined;
  }

  formatTerminalRunDetails(task: DispatchTaskDetails): TerminalRunTask {
    return {
      taskName: task.taskName,
      dropoffLocations: task.metadata.dropoffLocations,
      dropoffNotes: task.metadata.dropoffNotes,
      pickupLocations: task.metadata.pickupLocations,
      pickupNotes: task.metadata.pickupNotes
    };
  }

  saveFlightTaskCoordNotes(task: any, notes: string) {
    this.dispatchService.saveFlightTaskCoordNotes(task, this.taskDetailsList, notes).subscribe((res) => {
      // console.log(res);
    });
  }

  updateTaskTime(taskDetails: DispatchTaskDetails, type: 'plannedStartTime' | 'plannedEndTime', value: string): void {
    if (HH_MM_CTRLS.test(value)) {
      this.dispatchService
        .updateTaskTime(value ? value.padStart(5, '0') : '', taskDetails, type)
        .subscribe((response: DispatchUpdateResponse) => {
          this.logger.debug(`DispatchUpdateResponse:`, response, 'http');
        });
    } else {
      this.logger.debug(`DispatchUpdateResponse form error: Invalid input time detected`, 'ui');
    }
  }
}
